import { useCallback, useContext, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded'
import {
  createShape,
  DEFAULT_SHAPE_SIZE,
  DESIGN_CANVAS_GRID,
  isStrokeShape,
  shapeBoundingBox,
  type DesignCanvasSettings,
  type DesignConnectorShape,
  type DesignCurveShape,
  type DesignLineShape,
  type DesignPolylineShape,
  type DesignShape,
  type DesignShapeType,
} from '../../types/design'
import type { ResolvedNodeBinding } from '../../services/dataBindingService'
import { useToast } from '../../context/useToast'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { useOrbit } from '../Common/Orbit/useOrbit'
import { buildDesignCanvasSlices, buildDesignNodeSlices, buildRoleBadgeSlices } from './DesignCanvas.orbit'
import { withLcmChip } from '../Common/Orbit/lcmOrbitSlices'
import { useLcmChipDispatchers } from '../Common/Orbit/useLcmChipDispatchers'
import type { OrbitActionBridge } from '../Common/Orbit/orbitSliceHelpers'
import { AuthContext } from '../../context/authContextCore'
import { WorkspaceContext } from '../../context/workspaceContextCore'
import { resolveOrbitActionId } from '../Common/Orbit/orbitSliceIdMap'
import { invokeAction } from '../../services/actions/registry'
import { useXRay, resolveDesignAnchor } from '../Common/XRay'
import { portsForShape } from './shapes'
import { ShapeRenderer } from './ShapeRenderer'
import type { DesignEditorApi } from './useDesignEditor'
import { DesignCanvasBackdrop, designCanvasStyle, resolveDesignCanvasPreset } from './canvasPresets'
import { FloatingToolbar } from './FloatingToolbar'
import { cloneDesignShape, resizeDesignShape, rotateDesignShape, translateDesignShape } from './designGeometry'

type Props = {
  editor: DesignEditorApi
  bindings: Map<string, ResolvedNodeBinding>
  canvas?: DesignCanvasSettings | null
  readOnly?: boolean
  onRequestImage?: () => void
}

/** Which endpoint / control point of a stroke shape is being dragged. */
type StrokeHandleKind =
  | 'LINE_P1'
  | 'LINE_P2'
  | 'CURVE_P1'
  | 'CURVE_P2'
  | 'CURVE_C1'
  | 'CURVE_C2'
  | { kind: 'POLYLINE_VERTEX'; index: number }

type DrawState =
  | null
  | { kind: 'CREATE'; tool: DesignShapeType; start: { x: number; y: number }; current: { x: number; y: number } }
  | { kind: 'MARQUEE'; start: { x: number; y: number }; current: { x: number; y: number } }
  | { kind: 'MOVE'; startShapes: DesignShape[]; startPointer: { x: number; y: number } }
  | { kind: 'RESIZE'; handle: ResizeHandle; startBox: BoxRect; startPointer: { x: number; y: number }; startShapes: DesignShape[] }
  | { kind: 'ROTATE'; center: { x: number; y: number }; startAngle: number; startShapes: DesignShape[] }
  | { kind: 'PAN'; startPointer: { x: number; y: number }; startViewport: { x: number; y: number } }
  | { kind: 'FREEHAND'; shapeId: string; points: Array<{ x: number; y: number }> }
  | { kind: 'CONNECTOR'; fromShapeId: string; fromPort?: 'N' | 'S' | 'E' | 'W'; current: { x: number; y: number } }
  | { kind: 'LINE_DRAW'; shapeId: string; tool: 'LINE' | 'ARROW'; start: { x: number; y: number }; current: { x: number; y: number } }
  | { kind: 'POLYLINE_DRAW'; shapeId: string; points: Array<{ x: number; y: number }>; current: { x: number; y: number } }
  | { kind: 'CURVE_DRAW'; shapeId: string; start: { x: number; y: number }; current: { x: number; y: number } }
  | { kind: 'STROKE_HANDLE'; shapeId: string; handle: StrokeHandleKind }

type ResizeHandle = 'NW' | 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W'
type BoxRect = { x: number; y: number; width: number; height: number }

const newId = () => `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

const snap = (v: number, on: boolean) => on ? Math.round(v / DESIGN_CANVAS_GRID) * DESIGN_CANVAS_GRID : v
const CONNECTOR_SNAP_RADIUS = 18

const TEXT_EDITABLE_TYPES = new Set<string>([
  'RECT', 'ROUNDED_RECT', 'PROCESS', 'ELLIPSE', 'BUBBLE',
  'TRIANGLE', 'DIAMOND', 'PARALLELOGRAM', 'HEXAGON',
  'CYLINDER', 'DOCUMENT', 'TERMINATOR', 'OFF_PAGE',
  'POLYGON', 'STAR', 'TEXT', 'STICKY',
])

const ARROW_MARKER_DEFS = (
  <>
    <marker id="arrow-end-TRIANGLE-94a3b8" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
      <path d="M0,0 L10,5 L0,10 Z" fill="#94a3b8" />{/* mosaic-keep: user data */}
    </marker>
    <marker id="arrow-end-OPEN-94a3b8" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="10" markerHeight="10" orient="auto-start-reverse">
      <path d="M0,0 L10,5 L0,10" fill="none" stroke="#94a3b8" strokeWidth="2" />{/* mosaic-keep: user data */}
    </marker>
    <marker id="arrow-end-DIAMOND-94a3b8" viewBox="0 0 12 10" refX="11" refY="5" markerWidth="10" markerHeight="10" orient="auto-start-reverse">
      <path d="M0,5 L6,0 L12,5 L6,10 Z" fill="#94a3b8" />{/* mosaic-keep: user data */}
    </marker>
    <marker id="arrow-end-CIRCLE-94a3b8" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
      <circle cx="5" cy="5" r="4" fill="#94a3b8" />{/* mosaic-keep: user data */}
    </marker>
    <marker id="arrow-start-TRIANGLE-94a3b8" viewBox="0 0 10 10" refX="1" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
      <path d="M10,0 L0,5 L10,10 Z" fill="#94a3b8" />{/* mosaic-keep: user data */}
    </marker>
    <marker id="arrow-end-TRIANGLE-cbd5e1" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
      <path d="M0,0 L10,5 L0,10 Z" fill="#cbd5e1" />{/* mosaic-keep: user data */}
    </marker>
    <linearGradient id="progressGrad" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stopColor="#22c55e" />{/* mosaic-keep: user data */}
      <stop offset="100%" stopColor="#38bdf8" />{/* mosaic-keep: user data */}
    </linearGradient>
  </>
)

export const DesignCanvas = ({ editor, bindings, canvas, readOnly = false, onRequestImage }: Props) => {
  const { showToast } = useToast()
  const { open: openXRay } = useXRay()
  const lcmDispatchers = useLcmChipDispatchers()
  const auth = useContext(AuthContext)
  const workspace = useContext(WorkspaceContext)
  const workspaceId = workspace?.activeWorkspaceId ?? ''
  const actor = useMemo(() => ({ uid: auth?.currentUser?.uid ?? '' }), [auth?.currentUser?.uid])
  const canPerform = useMemo(() => workspace?.canPerform ?? (() => true), [workspace?.canPerform])
  const containerRef = useRef<HTMLDivElement | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [draw, setDraw] = useState<DrawState>(null)
  const [editingTextId, setEditingTextId] = useState<string | null>(null)
  const [hoverShapeId, setHoverShapeId] = useState<string | null>(null)
  const [size, setSize] = useState({ w: 800, h: 600 })
  const { state, setViewport, setSelection, toggleSelect, setTool, addShape, updateShapes, updateSingle, deleteShapes, beginInteraction, endInteraction } = editor
  const onOrbitAction = useCallback<OrbitActionBridge>((sliceId, label, id, title, phase6) => {
    if (phase6) {
      const anchor = resolveDesignAnchor({
        shapeId: id,
        shapes: state.shapes,
        sliceId,
      })
      if (anchor) {
        if (id) {
          const el = document.querySelector<HTMLElement>(`[data-shape-id="${id}"], [data-node-id="${id}"]`)
          if (el) {
            const rect = el.getBoundingClientRect()
            anchor.screenPosition = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
          }
        }
        openXRay(anchor, { sliceId, page: 'design' })
        return
      }
      showToast('Coming in Orbit Phase 6')
      return
    }
    const actionId = resolveOrbitActionId(sliceId, 'design')
    if (actionId) {
      void invokeAction(actionId, { id, title }, {
        workspaceId,
        actor,
        canPerform,
        showToast,
        invokedFrom: 'orbit',
        designOps: {
          deleteShape: (shapeId) => {
            if (shapeId) deleteShapes([shapeId])
          },
          duplicateShape: (shapeId) => {
            if (shapeId) {
              setSelection([shapeId])
              editor.duplicate()
            }
          },
          editShape: (shapeId) => {
            if (shapeId) setSelection([shapeId])
          },
          createNode: () => {
            editor.createAndAddShape('RECT', { x: 100, y: 100 })
          },
          reflow: () => {
            showToast('Layout reflowed', 'info')
          },
          changeRole: () => {
            showToast('Change role is ready', 'info')
          },
          cycleRole: () => {
            showToast('Cycle role is ready', 'info')
          },
          showDownstream: () => {
            showToast('Downstream highlighted', 'info')
          },
          detachRole: () => {
            showToast('Role detached', 'info')
          },
          showBindings: () => {
            showToast('Bindings inspected', 'info')
          },
        },
      })
      return
    }
    const lcmDispatch = lcmDispatchers[sliceId]
    if (lcmDispatch) {
      lcmDispatch(id, title)
      return
    }
    console.info('[orbit] unwired', sliceId)
    showToast(`${label}${title ? ` · ${title}` : ''}`)
    // TODO(orbit-handler-missing): sliceIds that hit this fallback still need real primitives — grep console for "[orbit] unwired" to enumerate.
  }, [actor, canPerform, deleteShapes, editor, lcmDispatchers, openXRay, setSelection, showToast, state.shapes, workspaceId])
  const orbitDescriptor = useMemo(() => ({
    page: 'design',
    centerLabel: 'Design',
    onCreate: () => { editor.createAndAddShape('RECT', { x: 100, y: 100 }) },
    contextSlices: () => [],
    targets: withLcmChip({
      node: (node: HTMLElement) => buildDesignNodeSlices(node, onOrbitAction),
      'role-badge': (node: HTMLElement) => buildRoleBadgeSlices(node, onOrbitAction),
      canvas: () => buildDesignCanvasSlices(onOrbitAction),
    }, onOrbitAction),
  }), [editor, onOrbitAction])
  useOrbit(orbitDescriptor)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect
      setSize({ w: r.width, h: r.height })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const shapesById = useMemo(() => new Map(state.shapes.map((s) => [s.id, s])), [state.shapes])
  const visibleShapes = useMemo(() => state.shapes.filter((s) => !s.hidden), [state.shapes])
  const visibleShapesById = useMemo(() => new Map(visibleShapes.map((s) => [s.id, s])), [visibleShapes])

  const screenToWorld = useCallback((sx: number, sy: number) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return { x: 0, y: 0 }
    const x = (sx - rect.left - state.viewport.x) / state.viewport.zoom
    const y = (sy - rect.top - state.viewport.y) / state.viewport.zoom
    return { x, y }
  }, [state.viewport])

  useEffect(() => {
    if (!editingTextId) return
    const frame = window.requestAnimationFrame(() => {
      const editable = svgRef.current?.querySelector<HTMLElement>(`[data-text-shape-id="${editingTextId}"]`)
      editable?.focus()
      const selection = window.getSelection()
      if (editable && selection) {
        const range = document.createRange()
        range.selectNodeContents(editable)
        range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
      }
    })
    return () => window.cancelAnimationFrame(frame)
  }, [editingTextId])

  const onWheel = useCallback((e: WheelEvent) => {
    if (!containerRef.current) return
    e.preventDefault()
    const rect = containerRef.current.getBoundingClientRect()
    const sx = e.clientX - rect.left
    const sy = e.clientY - rect.top
    // ctrlKey is set by the browser for trackpad pinch-zoom; regular wheel pans.
    if (e.ctrlKey || e.metaKey) {
      const wx = (sx - state.viewport.x) / state.viewport.zoom
      const wy = (sy - state.viewport.y) / state.viewport.zoom
      const next = Math.min(8, Math.max(0.1, state.viewport.zoom * Math.exp(-e.deltaY * 0.01)))
      setViewport({ zoom: next, x: sx - wx * next, y: sy - wy * next })
    } else {
      setViewport({ zoom: state.viewport.zoom, x: state.viewport.x - e.deltaX, y: state.viewport.y - e.deltaY })
    }
  }, [state.viewport, setViewport])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const handler = (e: WheelEvent) => onWheel(e)
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [onWheel])

  // Keyboard
  const [spaceDown, setSpaceDown] = useState(false)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) setSpaceDown(true)
      // Escape always exits text edit, even from inside the contentEditable.
      if (editingTextId && e.key === 'Escape') {
        e.preventDefault()
        setEditingTextId(null)
        return
      }
      if (editingTextId) return
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (state.selection.length > 0) {
          e.preventDefault()
          deleteShapes(state.selection)
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault()
        if (e.shiftKey) editor.redo()
        else editor.undo()
      } else if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || e.key === 'Z')) {
        e.preventDefault()
        editor.redo()
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'c') {
        editor.copy()
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
        editor.paste()
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault()
        editor.duplicate()
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault()
        setSelection(state.shapes.map((s) => s.id))
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'g') {
        e.preventDefault()
        if (e.shiftKey) editor.ungroup()
        else editor.group()
      } else if (e.key === 'Escape') {
        // Cancel an in-progress polyline session cleanly instead of leaving a partial shape.
        if (draw && draw.kind === 'POLYLINE_DRAW') {
          deleteShapes([draw.shapeId])
          setDraw(null)
          endInteraction()
        }
        setTool('SELECT')
        setSelection([])
        setEditingTextId(null)
      } else if (e.key === 'v' || e.key === 'V') {
        setTool('SELECT')
      } else if (e.key === 'r' || e.key === 'R') {
        setTool('RECT')
      } else if (e.key === 'o' || e.key === 'O') {
        setTool('ELLIPSE')
      } else if (e.key === 'l' || e.key === 'L') {
        setTool('LINE')
      } else if (e.key === 'a' && !e.metaKey && !e.ctrlKey) {
        setTool('ARROW')
      } else if (e.key === 't' || e.key === 'T') {
        setTool('TEXT')
      } else if (e.key === 'p' || e.key === 'P') {
        setTool('FREEHAND')
      } else if (e.key === 'n' || e.key === 'N') {
        setTool('STICKY')
      } else if (e.key === 'c' && !e.metaKey && !e.ctrlKey) {
        setTool('CONNECTOR')
      } else if (e.key === 'i' || e.key === 'I') {
        onRequestImage?.()
      }
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpaceDown(false)
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => { window.removeEventListener('keydown', onKeyDown); window.removeEventListener('keyup', onKeyUp) }
  }, [editor, state.shapes, state.selection, setSelection, deleteShapes, setTool, editingTextId, onRequestImage])

  const findNearestShape = useCallback((point: { x: number; y: number }, excludeId?: string | null) => {
    let best: { shape: DesignShape; port: 'N' | 'S' | 'E' | 'W'; distance: number } | null = null
    for (const shape of visibleShapes) {
      if (shape.id === excludeId || shape.type === 'CONNECTOR' || shape.type === 'FREEHAND' || shape.type === 'GROUP') continue
      const ports = portsForShape({ x: shape.position.x, y: shape.position.y, width: shape.size.width, height: shape.size.height })
      for (const port of ['N', 'S', 'E', 'W'] as const) {
        const candidate = ports[port]
        const distance = Math.hypot(candidate.x - point.x, candidate.y - point.y)
        if (!best || distance < best.distance) best = { shape, port, distance }
      }
      const inside = point.x >= shape.position.x && point.x <= shape.position.x + shape.size.width
        && point.y >= shape.position.y && point.y <= shape.position.y + shape.size.height
      if (inside) return { shape, port: 'AUTO' as const, distance: 0 }
    }
    if (!best || best.distance > CONNECTOR_SNAP_RADIUS / state.viewport.zoom) return null
    return best
  }, [state.viewport.zoom, visibleShapes])

  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (readOnly) return
    if ((e.target as Element).closest('[data-text-overlay]')) return
    const world = screenToWorld(e.clientX, e.clientY)
    const isPan = state.tool === 'PAN' || spaceDown || e.button === 1
    if (isPan) {
      setDraw({ kind: 'PAN', startPointer: { x: e.clientX, y: e.clientY }, startViewport: { x: state.viewport.x, y: state.viewport.y } })
      try { (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
      return
    }
    // Eraser tool — click any shape (or its stroke) to delete it. No drag.
    if (state.tool === 'ERASER') {
      const target = e.target as Element
      const strokeId = target.closest('[data-stroke-hit]')?.getAttribute('data-stroke-hit') ?? null
      const shapeId = strokeId ?? target.closest('[data-shape-id]')?.getAttribute('data-shape-id') ?? null
      if (shapeId) deleteShapes([shapeId])
      return
    }
    // Fill bucket — apply current palette colour to whatever was clicked.
    if (state.tool === 'FILL_BUCKET') {
      const target = e.target as Element
      const strokeId = target.closest('[data-stroke-hit]')?.getAttribute('data-stroke-hit') ?? null
      const shapeId = strokeId ?? target.closest('[data-shape-id]')?.getAttribute('data-shape-id') ?? null
      if (!shapeId) return
      const hit = shapesById.get(shapeId)
      if (!hit) return
      // Stroke shapes → paint the stroke; filled shapes → paint the fill.
      if (isStrokeShape(hit)) {
        updateSingle(shapeId, { stroke: { ...(hit.stroke ?? { width: 2, style: 'SOLID' }), color: state.currentColor } } as Partial<DesignShape>)
      } else {
        updateSingle(shapeId, { fill: { kind: 'SOLID', color: state.currentColor } } as Partial<DesignShape>)
      }
      return
    }
    if (state.tool === 'FREEHAND') {
      const id = newId()
      const s = createShape('FREEHAND', world, { width: 1, height: 1 }, id)
      if (s.stroke) s.stroke = { ...s.stroke, color: state.currentColor }
      beginInteraction()
      addShape({ ...s, points: [world] } as DesignShape, { select: true })
      setDraw({ kind: 'FREEHAND', shapeId: id, points: [world] })
      try { (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
      return
    }
    if (state.tool === 'LINE' || state.tool === 'ARROW') {
      const id = newId()
      const s = createShape(state.tool, world, DEFAULT_SHAPE_SIZE[state.tool], id) as DesignLineShape
      if (s.stroke) s.stroke = { ...s.stroke, color: state.currentColor }
      // Start p1/p2 at the same point; move handler updates p2 as the user drags.
      s.path = { variant: 'STRAIGHT', p1: { ...world }, p2: { ...world } }
      beginInteraction()
      addShape(s as DesignShape, { select: true })
      setDraw({ kind: 'LINE_DRAW', shapeId: id, tool: state.tool, start: world, current: world })
      try { (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
      return
    }
    if (state.tool === 'POLYLINE') {
      // If we're already in a polyline draw session, append the next vertex.
      if (draw && draw.kind === 'POLYLINE_DRAW') {
        const nextPoints = [...draw.points, world]
        updateSingle(draw.shapeId, { points: nextPoints } as Partial<DesignShape>)
        setDraw({ ...draw, points: nextPoints, current: world })
        return
      }
      const id = newId()
      const s = createShape('POLYLINE', world, DEFAULT_SHAPE_SIZE.POLYLINE, id) as DesignPolylineShape
      if (s.stroke) s.stroke = { ...s.stroke, color: state.currentColor }
      s.points = [world, world]
      beginInteraction()
      addShape(s as DesignShape, { select: true })
      setDraw({ kind: 'POLYLINE_DRAW', shapeId: id, points: [world], current: world })
      return
    }
    if (state.tool === 'CURVE') {
      const id = newId()
      const s = createShape('CURVE', world, DEFAULT_SHAPE_SIZE.CURVE, id) as DesignCurveShape
      if (s.stroke) s.stroke = { ...s.stroke, color: state.currentColor }
      s.p1 = { ...world }
      s.p2 = { ...world }
      s.c1 = { ...world }
      s.c2 = { ...world }
      beginInteraction()
      addShape(s as DesignShape, { select: true })
      setDraw({ kind: 'CURVE_DRAW', shapeId: id, start: world, current: world })
      try { (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
      return
    }
    // Endpoint/control-point drag on a stroke shape when SELECT is active.
    if (state.tool === 'SELECT') {
      const strokeHandle = (e.target as Element).closest('[data-stroke-handle]')?.getAttribute('data-stroke-handle')
      const strokeHandleShape = (e.target as Element).closest('[data-stroke-handle]')?.getAttribute('data-stroke-handle-shape') ?? null
      if (strokeHandle && strokeHandleShape) {
        beginInteraction()
        let parsed: StrokeHandleKind
        if (strokeHandle.startsWith('POLYLINE_VERTEX:')) {
          const index = Number(strokeHandle.split(':')[1])
          parsed = { kind: 'POLYLINE_VERTEX', index }
        } else {
          parsed = strokeHandle as StrokeHandleKind
        }
        setDraw({ kind: 'STROKE_HANDLE', shapeId: strokeHandleShape, handle: parsed })
        try { (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
        return
      }
    }
    if (state.tool === 'CONNECTOR') {
      const portTarget = (e.target as Element).closest('[data-port]')?.getAttribute('data-port') as 'N' | 'S' | 'E' | 'W' | null
      const portShapeId = (e.target as Element).closest('[data-port-shape]')?.getAttribute('data-port-shape') ?? null
      const target = (e.target as Element).closest('[data-shape-id]')
      const fromId = portShapeId ?? target?.getAttribute('data-shape-id') ?? findNearestShape(world)?.shape.id ?? null
      if (!fromId) return
      setDraw({ kind: 'CONNECTOR', fromShapeId: fromId, fromPort: portTarget ?? undefined, current: world })
      try { (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
      return
    }
    if (editor.isShapeTool(state.tool)) {
      if (state.tool === 'IMAGE') {
        onRequestImage?.()
        return
      }
      setDraw({ kind: 'CREATE', tool: state.tool, start: world, current: world })
      try { (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
      return
    }
    // SELECT tool — check what was hit
    const target = e.target as Element
    const handle = target.closest('[data-handle]')?.getAttribute('data-handle') as ResizeHandle | null
    if (handle && state.selection.length > 0) {
      beginInteraction()
      const sel = visibleShapes.filter((s) => state.selection.includes(s.id) && !s.locked)
      if (sel.length === 0) return
      const startBox = boundsOfShapes(sel)
      setDraw({ kind: 'RESIZE', handle, startBox, startPointer: world, startShapes: sel.map((s) => ({ ...s, position: { ...s.position }, size: { ...s.size } })) })
      try { (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
      return
    }
    if (target.closest('[data-rotate]')) {
      beginInteraction()
      const sel = visibleShapes.filter((s) => state.selection.includes(s.id) && !s.locked)
      if (sel.length === 0) return
      const b = boundsOfShapes(sel)
      const center = { x: b.x + b.width / 2, y: b.y + b.height / 2 }
      const angle = Math.atan2(world.y - center.y, world.x - center.x)
      setDraw({ kind: 'ROTATE', center, startAngle: angle - (sel[0]?.rotation ?? 0) * Math.PI / 180, startShapes: sel.map((s) => ({ ...s })) })
      try { (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
      return
    }
    const shapeId = (target.closest('[data-shape-id]') as Element | null)?.getAttribute('data-shape-id') ?? null
    const portTarget = (target.closest('[data-port]') as Element | null)?.getAttribute('data-port') as 'N' | 'S' | 'E' | 'W' | null
    const portShapeId = (target.closest('[data-port-shape]') as Element | null)?.getAttribute('data-port-shape') ?? null
    if (portShapeId && portTarget) {
      setDraw({ kind: 'CONNECTOR', fromShapeId: portShapeId, fromPort: portTarget, current: world })
      try { (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
      return
    }
    if (shapeId) {
      const hitShape = shapesById.get(shapeId)
      if (hitShape?.locked) {
        setSelection([shapeId])
        return
      }
      // Figure out the effective post-click selection.
      let nextSelection: string[]
      if (e.shiftKey) {
        nextSelection = state.selection.includes(shapeId)
          ? state.selection.filter((id) => id !== shapeId)
          : [...state.selection, shapeId]
        toggleSelect(shapeId)
      } else if (state.selection.includes(shapeId)) {
        nextSelection = state.selection
      } else {
        nextSelection = [shapeId]
        setSelection([shapeId])
      }
      // If the shift-click deselected the hit shape, don't start a drag.
      if (!nextSelection.includes(shapeId)) return
      beginInteraction()
      const sel = visibleShapes.filter((s) => nextSelection.includes(s.id) && !s.locked)
      setDraw({ kind: 'MOVE', startShapes: sel.map(cloneDesignShape), startPointer: world })
      try { (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId) } catch { /* ignore */ }
      return
    }
    // empty space — marquee
    setSelection([])
    setEditingTextId(null)
    setDraw({ kind: 'MARQUEE', start: world, current: world })
    ;(e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!draw) {
      const target = e.target as Element
      const hovered = target.closest('[data-shape-id]')?.getAttribute('data-shape-id') ?? null
      if (hovered !== hoverShapeId) setHoverShapeId(hovered)
      return
    }
    if (draw.kind === 'PAN') {
      setViewport({ zoom: state.viewport.zoom, x: draw.startViewport.x + (e.clientX - draw.startPointer.x), y: draw.startViewport.y + (e.clientY - draw.startPointer.y) })
      return
    }
    const world = screenToWorld(e.clientX, e.clientY)
    if (draw.kind === 'CREATE') {
      // Shift constrains rectangle → square, ellipse → circle, line/arrow → 45° angles.
      let cx = world.x, cy = world.y
      if (e.shiftKey) {
        const dx = world.x - draw.start.x
        const dy = world.y - draw.start.y
        if (draw.tool === 'LINE' || draw.tool === 'ARROW') {
          const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4)
          const len = Math.hypot(dx, dy)
          cx = draw.start.x + Math.cos(angle) * len
          cy = draw.start.y + Math.sin(angle) * len
        } else {
          const side = Math.max(Math.abs(dx), Math.abs(dy))
          cx = draw.start.x + Math.sign(dx || 1) * side
          cy = draw.start.y + Math.sign(dy || 1) * side
        }
      }
      setDraw({ ...draw, current: { x: cx, y: cy } })
    } else if (draw.kind === 'MARQUEE') {
      setDraw({ ...draw, current: world })
    } else if (draw.kind === 'FREEHAND') {
      const points = [...draw.points, world]
      setDraw({ ...draw, points })
      updateSingle(draw.shapeId, { points } as Partial<DesignShape>)
    } else if (draw.kind === 'LINE_DRAW') {
      let end = { ...world }
      if (e.shiftKey) {
        const dx = world.x - draw.start.x
        const dy = world.y - draw.start.y
        const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4)
        const len = Math.hypot(dx, dy)
        end = { x: draw.start.x + Math.cos(angle) * len, y: draw.start.y + Math.sin(angle) * len }
      }
      updateSingle(draw.shapeId, { path: { variant: 'STRAIGHT', p1: draw.start, p2: end } } as Partial<DesignShape>)
      setDraw({ ...draw, current: end })
    } else if (draw.kind === 'POLYLINE_DRAW') {
      // Live-preview the next vertex as the cursor moves.
      const preview = [...draw.points, world]
      updateSingle(draw.shapeId, { points: preview } as Partial<DesignShape>)
      setDraw({ ...draw, current: world })
    } else if (draw.kind === 'CURVE_DRAW') {
      const p1 = draw.start
      const p2 = { ...world }
      const c1 = { x: p1.x + (p2.x - p1.x) / 3, y: p1.y + (p2.y - p1.y) / 3 - 40 }
      const c2 = { x: p1.x + ((p2.x - p1.x) * 2) / 3, y: p1.y + ((p2.y - p1.y) * 2) / 3 - 40 }
      updateSingle(draw.shapeId, { p1, p2, c1, c2 } as Partial<DesignShape>)
      setDraw({ ...draw, current: world })
    } else if (draw.kind === 'STROKE_HANDLE') {
      const shape = shapesById.get(draw.shapeId)
      if (!shape) return
      if ((shape.type === 'LINE' || shape.type === 'ARROW') && shape.path) {
        const p1 = draw.handle === 'LINE_P1' ? world : shape.path.p1
        const p2 = draw.handle === 'LINE_P2' ? world : shape.path.p2
        updateSingle(shape.id, { path: { variant: 'STRAIGHT', p1, p2 } } as Partial<DesignShape>)
      } else if (shape.type === 'CURVE') {
        const patch: Partial<DesignCurveShape> = {}
        if (draw.handle === 'CURVE_P1') patch.p1 = world
        if (draw.handle === 'CURVE_P2') patch.p2 = world
        if (draw.handle === 'CURVE_C1') patch.c1 = world
        if (draw.handle === 'CURVE_C2') patch.c2 = world
        updateSingle(shape.id, patch as Partial<DesignShape>)
      } else if (shape.type === 'POLYLINE' && typeof draw.handle === 'object') {
        const idx = draw.handle.index
        const next = shape.points.map((p, i) => (i === idx ? world : p))
        updateSingle(shape.id, { points: next } as Partial<DesignShape>)
      }
    } else if (draw.kind === 'CONNECTOR') {
      setDraw({ ...draw, current: world })
    } else if (draw.kind === 'MOVE') {
      let dx = world.x - draw.startPointer.x
      let dy = world.y - draw.startPointer.y
      // Shift constrains movement to the dominant axis (Figma).
      if (e.shiftKey) {
        if (Math.abs(dx) > Math.abs(dy)) dy = 0
        else dx = 0
      }
      const updates = draw.startShapes.map((s) => ({
        id: s.id,
        patch: translateDesignShape(s, dx, dy, (point) => ({
          x: snap(point.x, state.snapToGrid),
          y: snap(point.y, state.snapToGrid),
        })),
      }))
      updateShapes(updates)
    } else if (draw.kind === 'RESIZE') {
      const { handle, startBox, startPointer, startShapes } = draw
      let nx = startBox.x, ny = startBox.y, nw = startBox.width, nh = startBox.height
      const dx = world.x - startPointer.x
      const dy = world.y - startPointer.y
      const MIN = 8
      const isCorner = handle.length === 2
      const lockAspect = e.shiftKey && isCorner
      const fromCenter = e.altKey
      // West / East — pin the opposite edge and clamp the near edge.
      if (handle.includes('W')) {
        nw = Math.max(MIN, startBox.width - dx)
        nx = startBox.x + startBox.width - nw
      } else if (handle.includes('E')) {
        nw = Math.max(MIN, startBox.width + dx)
      }
      if (handle.includes('N')) {
        nh = Math.max(MIN, startBox.height - dy)
        ny = startBox.y + startBox.height - nh
      } else if (handle.includes('S')) {
        nh = Math.max(MIN, startBox.height + dy)
      }
      // Shift on corner handles → keep original aspect ratio (Figma).
      if (lockAspect && startBox.width > 0 && startBox.height > 0) {
        const targetAspect = startBox.width / startBox.height
        if (nw / nh > targetAspect) {
          nw = nh * targetAspect
        } else {
          nh = nw / targetAspect
        }
        if (handle.includes('W')) nx = startBox.x + startBox.width - nw
        if (handle.includes('N')) ny = startBox.y + startBox.height - nh
      }
      // Alt → resize about the center of the original box (Figma / Paint).
      if (fromCenter) {
        const cxb = startBox.x + startBox.width / 2
        const cyb = startBox.y + startBox.height / 2
        if (handle.includes('E') || handle.includes('W')) {
          const halfNew = Math.max(MIN / 2, Math.abs((handle.includes('E') ? world.x - cxb : cxb - world.x)))
          nw = halfNew * 2
          nx = cxb - halfNew
        }
        if (handle.includes('N') || handle.includes('S')) {
          const halfNew = Math.max(MIN / 2, Math.abs((handle.includes('S') ? world.y - cyb : cyb - world.y)))
          nh = halfNew * 2
          ny = cyb - halfNew
        }
      }
      const nextBox = { x: nx, y: ny, width: nw, height: nh }
      const updates = startShapes.map((s) => ({
        id: s.id,
        patch: resizeDesignShape(s, startBox, nextBox),
      }))
      updateShapes(updates)
    } else if (draw.kind === 'ROTATE') {
      const angle = Math.atan2(world.y - draw.center.y, world.x - draw.center.x)
      let deg = ((angle - draw.startAngle) * 180) / Math.PI
      // Shift → snap rotation to 15° increments (Figma).
      if (e.shiftKey) deg = Math.round(deg / 15) * 15
      const updates = draw.startShapes.map((s) => ({ id: s.id, patch: rotateDesignShape(s, draw.center, deg) }))
      updateShapes(updates)
    }
  }

  const onPointerUp = (e: ReactPointerEvent<SVGSVGElement>) => {
    try { (e.currentTarget as SVGSVGElement).releasePointerCapture(e.pointerId) } catch { /* ignore */ }
    if (!draw) return
    if (draw.kind === 'CREATE') {
      const rawW = Math.abs(draw.current.x - draw.start.x)
      const rawH = Math.abs(draw.current.y - draw.start.y)
      // Click-without-drag → use the tool's default size, centered on the click.
      const tinyClick = rawW < 4 && rawH < 4
      const defaults = DEFAULT_SHAPE_SIZE[draw.tool]
      const w = tinyClick ? defaults.width : Math.max(8, rawW)
      const h = tinyClick ? defaults.height : Math.max(8, rawH)
      const rawX = tinyClick ? draw.start.x - w / 2 : Math.min(draw.start.x, draw.current.x)
      const rawY = tinyClick ? draw.start.y - h / 2 : Math.min(draw.start.y, draw.current.y)
      const created = editor.createAndAddShape(
        draw.tool,
        { x: snap(rawX, state.snapToGrid), y: snap(rawY, state.snapToGrid) },
        { width: w, height: h },
      )
      // TEXT / STICKY drop → immediately enter edit mode so the user can type.
      if (draw.tool === 'TEXT' || draw.tool === 'STICKY') setEditingTextId(created.id)
    } else if (draw.kind === 'MARQUEE') {
      const x1 = Math.min(draw.start.x, draw.current.x)
      const y1 = Math.min(draw.start.y, draw.current.y)
      const x2 = Math.max(draw.start.x, draw.current.x)
      const y2 = Math.max(draw.start.y, draw.current.y)
      const dragged = Math.abs(draw.current.x - draw.start.x) > 2 || Math.abs(draw.current.y - draw.start.y) > 2
      if (dragged) {
        const hits = state.shapes.filter((s) => {
          const b = { x: s.position.x, y: s.position.y, w: s.size.width, h: s.size.height }
          return b.x < x2 && b.x + b.w > x1 && b.y < y2 && b.y + b.h > y1
        }).map((s) => s.id)
        const isAdditive = e.shiftKey
        if (isAdditive) {
          const merged = Array.from(new Set([...state.selection, ...hits]))
          setSelection(merged)
        } else {
          setSelection(hits)
        }
      }
    } else if (draw.kind === 'FREEHAND') {
      const points = draw.points
      if (points.length < 2) {
        deleteShapes([draw.shapeId])
      } else {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (const p of points) { minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y) }
        updateSingle(draw.shapeId, { position: { x: minX, y: minY }, size: { width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) } } as Partial<DesignShape>)
      }
    } else if (draw.kind === 'LINE_DRAW') {
      const dist = Math.hypot(draw.current.x - draw.start.x, draw.current.y - draw.start.y)
      if (dist < 4) {
        // Tiny click — discard the accidental zero-length line.
        deleteShapes([draw.shapeId])
      } else {
        // Also stamp position/size so bbox-dependent code (marquee, delete-key overlays) stays honest.
        const minX = Math.min(draw.start.x, draw.current.x)
        const minY = Math.min(draw.start.y, draw.current.y)
        const w = Math.max(1, Math.abs(draw.current.x - draw.start.x))
        const h = Math.max(1, Math.abs(draw.current.y - draw.start.y))
        updateSingle(draw.shapeId, { position: { x: minX, y: minY }, size: { width: w, height: h } } as Partial<DesignShape>)
      }
    } else if (draw.kind === 'CURVE_DRAW') {
      const shape = shapesById.get(draw.shapeId)
      if (!shape || shape.type !== 'CURVE') { setDraw(null); return }
      const dist = Math.hypot(draw.current.x - draw.start.x, draw.current.y - draw.start.y)
      if (dist < 4) {
        deleteShapes([draw.shapeId])
      } else {
        const minX = Math.min(shape.p1.x, shape.p2.x, shape.c1.x, shape.c2.x)
        const minY = Math.min(shape.p1.y, shape.p2.y, shape.c1.y, shape.c2.y)
        const maxX = Math.max(shape.p1.x, shape.p2.x, shape.c1.x, shape.c2.x)
        const maxY = Math.max(shape.p1.y, shape.p2.y, shape.c1.y, shape.c2.y)
        updateSingle(shape.id, { position: { x: minX, y: minY }, size: { width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) } } as Partial<DesignShape>)
      }
    } else if (draw.kind === 'POLYLINE_DRAW') {
      // Polyline stays live until double-click; pointer-up alone doesn't finish it.
      return
    } else if (draw.kind === 'STROKE_HANDLE') {
      // Refresh position/size from the new geometry so marquee bbox stays consistent.
      const shape = shapesById.get(draw.shapeId)
      if (shape) {
        const pts = shape.type === 'LINE' || shape.type === 'ARROW'
          ? shape.path ? [shape.path.p1, shape.path.p2] : null
          : shape.type === 'CURVE'
            ? [shape.p1, shape.c1, shape.c2, shape.p2]
            : shape.type === 'POLYLINE'
              ? shape.points
              : null
        if (pts && pts.length > 0) {
          const minX = Math.min(...pts.map((p) => p.x))
          const minY = Math.min(...pts.map((p) => p.y))
          const maxX = Math.max(...pts.map((p) => p.x))
          const maxY = Math.max(...pts.map((p) => p.y))
          updateSingle(shape.id, { position: { x: minX, y: minY }, size: { width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) } } as Partial<DesignShape>)
        }
      }
    } else if (draw.kind === 'CONNECTOR') {
      const target = (e.target as Element).closest('[data-shape-id]')
      const explicitTargetId = target?.getAttribute('data-shape-id') ?? null
      const portTarget = (e.target as Element).closest('[data-port]')?.getAttribute('data-port') as 'N' | 'S' | 'E' | 'W' | null
      const snapped = explicitTargetId && explicitTargetId !== draw.fromShapeId
        ? { shape: visibleShapesById.get(explicitTargetId) ?? null, port: portTarget ?? 'AUTO' }
        : findNearestShape(draw.current, draw.fromShapeId)
      const targetId = snapped?.shape?.id ?? null
      const resolvedPort = ((snapped?.port === 'AUTO' ? portTarget ?? 'AUTO' : snapped?.port) ?? portTarget ?? 'AUTO') as 'AUTO' | 'N' | 'S' | 'E' | 'W'
      if (!targetId || targetId === draw.fromShapeId) {
        // free endpoint
        const id = newId()
        const fromShape = visibleShapesById.get(draw.fromShapeId)
        if (fromShape) {
          const conn = createShape('CONNECTOR', { x: 0, y: 0 }, { width: 1, height: 1 }, id) as DesignConnectorShape
          conn.from = { kind: 'SHAPE', shapeId: draw.fromShapeId, port: draw.fromPort ?? 'AUTO' }
          conn.to = { kind: 'FREE', x: draw.current.x, y: draw.current.y }
          addShape(conn, { select: true })
        }
      } else {
        const id = newId()
        const conn = createShape('CONNECTOR', { x: 0, y: 0 }, { width: 1, height: 1 }, id) as DesignConnectorShape
        conn.from = { kind: 'SHAPE', shapeId: draw.fromShapeId, port: draw.fromPort ?? 'AUTO' }
        conn.to = { kind: 'SHAPE', shapeId: targetId, port: resolvedPort }
        addShape(conn, { select: true })
      }
    }
    setDraw(null)
    endInteraction()
  }

  const viewportRect = {
    x: -state.viewport.x / state.viewport.zoom,
    y: -state.viewport.y / state.viewport.zoom,
    width: size.w / state.viewport.zoom,
    height: size.h / state.viewport.zoom,
  }
  const viewBox = `${viewportRect.x} ${viewportRect.y} ${viewportRect.width} ${viewportRect.height}`
  const canvasPreset = resolveDesignCanvasPreset(canvas)

  const selectionBounds = useMemo(() => {
    if (state.selection.length === 0) return null
    const sel = visibleShapes.filter((s) => state.selection.includes(s.id))
    if (sel.length === 0) return null
    return boundsOfShapes(sel)
  }, [state.selection, visibleShapes])

  const handlePositions = (b: BoxRect): Array<{ key: ResizeHandle; x: number; y: number; cursor: string }> => [
    { key: 'NW', x: b.x, y: b.y, cursor: 'nwse-resize' },
    { key: 'N', x: b.x + b.width / 2, y: b.y, cursor: 'ns-resize' },
    { key: 'NE', x: b.x + b.width, y: b.y, cursor: 'nesw-resize' },
    { key: 'E', x: b.x + b.width, y: b.y + b.height / 2, cursor: 'ew-resize' },
    { key: 'SE', x: b.x + b.width, y: b.y + b.height, cursor: 'nwse-resize' },
    { key: 'S', x: b.x + b.width / 2, y: b.y + b.height, cursor: 'ns-resize' },
    { key: 'SW', x: b.x, y: b.y + b.height, cursor: 'nesw-resize' },
    { key: 'W', x: b.x, y: b.y + b.height / 2, cursor: 'ew-resize' },
  ]

  const smartGuides = useMemo(() => {
    if (!draw || draw.kind !== 'MOVE' || !state.snapToObjects) return [] as Array<{ x1: number; y1: number; x2: number; y2: number }>
    const sel = visibleShapes.filter((s) => state.selection.includes(s.id))
    if (sel.length === 0) return []
    const moving = boundsOfShapes(sel)
    const others = visibleShapes.filter((s) => !state.selection.includes(s.id))
    const lines: Array<{ x1: number; y1: number; x2: number; y2: number }> = []
    for (const o of others) {
      const ob = shapeBoundingBox(o)
      const b = { x: ob.x, y: ob.y, w: ob.width, h: ob.height }
      if (Math.abs(moving.x - b.x) < 2) lines.push({ x1: b.x, y1: Math.min(moving.y, b.y) - 24, x2: b.x, y2: Math.max(moving.y + moving.height, b.y + b.h) + 24 })
      if (Math.abs(moving.x + moving.width - (b.x + b.w)) < 2) lines.push({ x1: b.x + b.w, y1: Math.min(moving.y, b.y) - 24, x2: b.x + b.w, y2: Math.max(moving.y + moving.height, b.y + b.h) + 24 })
      if (Math.abs(moving.y - b.y) < 2) lines.push({ x1: Math.min(moving.x, b.x) - 24, y1: b.y, x2: Math.max(moving.x + moving.width, b.x + b.w) + 24, y2: b.y })
    }
    return lines
  }, [draw, state.snapToObjects, visibleShapes, state.selection])

  const cursor = (() => {
    if (spaceDown || state.tool === 'PAN') return 'grab'
    if (state.tool === 'ERASER') return 'not-allowed'
    if (state.tool === 'FILL_BUCKET') return 'copy'
    if (state.tool === 'FREEHAND') return 'crosshair'
    if (editor.isShapeTool(state.tool) || (state.tool as string) === 'CONNECTOR') return 'crosshair'
    return 'default'
  })()

  return (
    <div ref={containerRef} data-orbit-target="canvas" className="dh-canvas-host" style={{ ...designCanvasStyle(canvas), cursor }}>
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={viewBox}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={(e) => {
          // End polyline session on double-click.
          if (draw && draw.kind === 'POLYLINE_DRAW') {
            const finalPoints = draw.points.length >= 2 ? draw.points : null
            if (finalPoints) {
              updateSingle(draw.shapeId, { points: finalPoints } as Partial<DesignShape>)
              const minX = Math.min(...finalPoints.map((p) => p.x))
              const minY = Math.min(...finalPoints.map((p) => p.y))
              const maxX = Math.max(...finalPoints.map((p) => p.x))
              const maxY = Math.max(...finalPoints.map((p) => p.y))
              updateSingle(draw.shapeId, { position: { x: minX, y: minY }, size: { width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) } } as Partial<DesignShape>)
            } else {
              deleteShapes([draw.shapeId])
            }
            setDraw(null)
            endInteraction()
            return
          }
          const id = (e.target as Element).closest('[data-shape-id]')?.getAttribute('data-shape-id')
          if (!id) return
          const s = shapesById.get(id)
          if (s && TEXT_EDITABLE_TYPES.has(s.type)) setEditingTextId(id)
        }}
      >
        <defs>{ARROW_MARKER_DEFS}</defs>
        <DesignCanvasBackdrop canvas={canvas} x={viewportRect.x} y={viewportRect.y} width={viewportRect.width} height={viewportRect.height} />
        {state.showGrid && (
          <GridLayer viewport={state.viewport} size={size} grid={canvas?.grid ?? canvasPreset.grid} />
        )}
        {visibleShapes.map((s) => {
          const linked = s.binding?.linkedNodeId ?? null
          const resolved = linked ? bindings.get(linked) ?? null : null
          return (
            <g key={s.id} data-shape-id={s.id} data-orbit-target="node" data-node-id={s.id} data-orbit-title={s.text || s.type} style={{ cursor: state.tool === 'SELECT' ? 'move' : undefined }}>
              <ShapeRenderer shape={s} shapesById={visibleShapesById} resolved={resolved} editing={editingTextId === s.id} onTextChange={(text) => updateSingle(s.id, { text } as Partial<DesignShape>)} />
              {/* Filled shapes: full AABB is clickable so anywhere-in-the-shape selects. */}
              {!isStrokeShape(s) && (
                <rect x={s.position.x} y={s.position.y} width={s.size.width} height={s.size.height} fill="transparent" pointerEvents="all" />
              )}
              {/* Stroke shapes: fat invisible hit-stroke follows the actual geometry so only clicking ON the stroke selects. */}
              {isStrokeShape(s) && s.type !== 'CONNECTOR' && (
                <StrokeHitOverlay shape={s} zoom={state.viewport.zoom} />
              )}
            </g>
          )
        })}
        {/* Canva-style connection ports remain visible for selected/hovered shapes. */}
        {(state.tool === 'SELECT' || state.tool === 'CONNECTOR') && visibleShapes
          .filter((s) => (s.id === hoverShapeId || state.selection.includes(s.id)) && s.type !== 'CONNECTOR' && s.type !== 'FREEHAND' && s.type !== 'GROUP')
          .map((s) => {
            const ports = portsForShape({ x: s.position.x, y: s.position.y, width: s.size.width, height: s.size.height })
            return (
              <g key={`ports-${s.id}`} data-port-shape={s.id}>
                {(['N', 'S', 'E', 'W'] as const).map((p) => (
                  <circle
                    key={p}
                    data-port={p}
                    cx={ports[p].x}
                    cy={ports[p].y}
                    r={8 / state.viewport.zoom}
                    fill="var(--accent-primary)"
                    stroke="var(--surface-raised)"
                    strokeWidth={2 / state.viewport.zoom}
                    className="dh-connector-port"
                    style={{ cursor: 'crosshair' }}
                  />
                ))}
              </g>
            )
          })}
        {/* Smart guides */}
        {smartGuides.map((l, i) => (
          <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="var(--accent-primary)" strokeWidth={1 / state.viewport.zoom} strokeDasharray={`${4 / state.viewport.zoom} ${3 / state.viewport.zoom}`} />
        ))}
        {/* Selection box & handles. For stroke-shape-only selections we skip the AABB box,
             the 8 resize handles, and the rotate grip — those don't make sense for lines/pencil.
             We render endpoint / control-point handles instead so the user can nudge geometry directly. */}
        {selectionBounds && (() => {
          const selectedShapes = visibleShapes.filter((s) => state.selection.includes(s.id))
          const strokeOnly = selectedShapes.length > 0 && selectedShapes.every(isStrokeShape)
          if (strokeOnly) {
            return (
              <g>
                {selectedShapes.map((s) => (
                  <StrokeHandles key={`sh-${s.id}`} shape={s} zoom={state.viewport.zoom} />
                ))}
              </g>
            )
          }
          return (
            <g>
              <rect pointerEvents="none" x={selectionBounds.x} y={selectionBounds.y} width={selectionBounds.width} height={selectionBounds.height} fill="none" stroke="color-mix(in srgb, var(--accent-primary) 75%, transparent)" strokeWidth={1.25 / state.viewport.zoom} />
              <line pointerEvents="none" x1={selectionBounds.x + selectionBounds.width / 2} y1={selectionBounds.y} x2={selectionBounds.x + selectionBounds.width / 2} y2={selectionBounds.y - 26 / state.viewport.zoom} stroke="color-mix(in srgb, var(--accent-primary) 70%, transparent)" strokeWidth={1.25 / state.viewport.zoom} />
              {handlePositions(selectionBounds).map((h) => (
                <rect key={h.key} data-handle={h.key} x={h.x - 4 / state.viewport.zoom} y={h.y - 4 / state.viewport.zoom} width={8 / state.viewport.zoom} height={8 / state.viewport.zoom} fill="var(--surface-raised)" stroke="color-mix(in srgb, var(--accent-primary) 85%, transparent)" strokeWidth={1.25 / state.viewport.zoom} style={{ cursor: h.cursor }} />
              ))}
              <circle data-rotate cx={selectionBounds.x + selectionBounds.width / 2} cy={selectionBounds.y - 30 / state.viewport.zoom} r={6 / state.viewport.zoom} fill="var(--surface-raised)" stroke="color-mix(in srgb, var(--accent-primary) 85%, transparent)" strokeWidth={1.25 / state.viewport.zoom} style={{ cursor: 'grab' }} />
            </g>
          )
        })()}
        {/* Marquee */}
        {draw && draw.kind === 'MARQUEE' && (
          <rect
            x={Math.min(draw.start.x, draw.current.x)}
            y={Math.min(draw.start.y, draw.current.y)}
            width={Math.abs(draw.current.x - draw.start.x)}
            height={Math.abs(draw.current.y - draw.start.y)}
            fill="color-mix(in srgb, var(--accent-primary) 8%, transparent)"
            stroke="var(--accent-primary)"
            strokeWidth={1 / state.viewport.zoom}
            strokeDasharray={`${4 / state.viewport.zoom} ${3 / state.viewport.zoom}`}
          />
        )}
        {/* Create preview */}
        {draw && draw.kind === 'CREATE' && (
          <rect
            x={Math.min(draw.start.x, draw.current.x)}
            y={Math.min(draw.start.y, draw.current.y)}
            width={Math.abs(draw.current.x - draw.start.x)}
            height={Math.abs(draw.current.y - draw.start.y)}
            fill="color-mix(in srgb, var(--accent-primary) 18%, transparent)"
            stroke="var(--accent-primary)"
            strokeWidth={1.5 / state.viewport.zoom}
            strokeDasharray={`${4 / state.viewport.zoom} ${3 / state.viewport.zoom}`}
          />
        )}
        {/* Connector preview */}
        {draw && draw.kind === 'CONNECTOR' && (() => {
          const from = visibleShapesById.get(draw.fromShapeId)
          if (!from) return null
          const ports = portsForShape({ x: from.position.x, y: from.position.y, width: from.size.width, height: from.size.height })
          const a = draw.fromPort ? ports[draw.fromPort] : { x: from.position.x + from.size.width / 2, y: from.position.y + from.size.height / 2 }
          return <line x1={a.x} y1={a.y} x2={draw.current.x} y2={draw.current.y} stroke="var(--accent-primary)" strokeWidth={2 / state.viewport.zoom} strokeDasharray={`${4 / state.viewport.zoom} ${3 / state.viewport.zoom}`} />
        })()}
        {visibleShapes.length === 0 && (() => {
          const cx = (-state.viewport.x + size.w / 2) / state.viewport.zoom
          const cy = (-state.viewport.y + size.h / 2) / state.viewport.zoom
          return (
            <g pointerEvents="none">
              <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fill="var(--muted)" fontSize={14 / state.viewport.zoom}>Pick a tool from the rail and drag on the canvas</text>
            </g>
          )
        })()}
      </svg>
      {!readOnly && !editingTextId && (
        <FloatingToolbar
          editor={editor}
          selectionBounds={selectionBounds}
          viewport={state.viewport}
          hostSize={size}
        />
      )}
      <ZoomBadge zoom={state.viewport.zoom} onSet={(z) => setViewport({ ...state.viewport, zoom: z })} />
    </div>
  )
}

/**
 * Fat-invisible hit stroke that follows a stroke shape's actual geometry, so
 * only clicking ON the drawn stroke selects it. Replaces the old bounding-box
 * click zone that used to grab clicks in empty space near line/pencil/arrow.
 */
const StrokeHitOverlay = ({ shape, zoom }: { shape: DesignShape; zoom: number }) => {
  const hitWidth = Math.max(10, 12 / Math.max(0.4, zoom))
  const common = { fill: 'none', stroke: 'transparent', strokeWidth: hitWidth, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, pointerEvents: 'stroke' as const, 'data-stroke-hit': shape.id }
  if (shape.type === 'LINE' || shape.type === 'ARROW') {
    const p1 = shape.path?.p1 ?? { x: shape.position.x, y: shape.position.y + shape.size.height / 2 }
    const p2 = shape.path?.p2 ?? { x: shape.position.x + shape.size.width, y: shape.position.y + shape.size.height / 2 }
    return <line {...common} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} />
  }
  if (shape.type === 'POLYLINE') {
    const points = shape.points.map((p) => `${p.x},${p.y}`).join(' ')
    return <polyline {...common} points={points} />
  }
  if (shape.type === 'CURVE') {
    const d = `M ${shape.p1.x} ${shape.p1.y} C ${shape.c1.x} ${shape.c1.y}, ${shape.c2.x} ${shape.c2.y}, ${shape.p2.x} ${shape.p2.y}`
    return <path {...common} d={d} />
  }
  if (shape.type === 'FREEHAND') {
    if (!shape.points || shape.points.length < 2) return null
    const d = shape.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
    return <path {...common} d={d} />
  }
  return null
}

/**
 * Draggable endpoint / control-point handles for LINE/ARROW/POLYLINE/CURVE.
 * Freehand intentionally has none — the property panel shows a Redraw button.
 */
const StrokeHandles = ({ shape, zoom }: { shape: DesignShape; zoom: number }) => {
  const size = 6 / zoom
  const stroke = 1.5 / zoom
  const cRadius = 5 / zoom
  const guideStroke = 1 / zoom
  const dot = (x: number, y: number, handle: string) => (
    <rect
      key={handle}
      data-stroke-handle={handle}
      data-stroke-handle-shape={shape.id}
      x={x - size}
      y={y - size}
      width={size * 2}
      height={size * 2}
      fill="var(--surface-raised)"
      stroke="var(--accent-primary)"
      strokeWidth={stroke}
      style={{ cursor: 'grab' }}
    />
  )
  const control = (x: number, y: number, handle: string) => (
    <circle
      key={handle}
      data-stroke-handle={handle}
      data-stroke-handle-shape={shape.id}
      cx={x}
      cy={y}
      r={cRadius}
      fill="var(--surface-raised)"
      stroke="var(--warning)"
      strokeWidth={stroke}
      style={{ cursor: 'grab' }}
    />
  )
  if ((shape.type === 'LINE' || shape.type === 'ARROW') && shape.path) {
    return (
      <g pointerEvents="all">
        {dot(shape.path.p1.x, shape.path.p1.y, 'LINE_P1')}
        {dot(shape.path.p2.x, shape.path.p2.y, 'LINE_P2')}
      </g>
    )
  }
  if (shape.type === 'CURVE') {
    return (
      <g pointerEvents="all">
        <line x1={shape.p1.x} y1={shape.p1.y} x2={shape.c1.x} y2={shape.c1.y} stroke="var(--warning)" strokeWidth={guideStroke} strokeDasharray={`${3 / zoom} ${3 / zoom}`} pointerEvents="none" />
        <line x1={shape.p2.x} y1={shape.p2.y} x2={shape.c2.x} y2={shape.c2.y} stroke="var(--warning)" strokeWidth={guideStroke} strokeDasharray={`${3 / zoom} ${3 / zoom}`} pointerEvents="none" />
        {dot(shape.p1.x, shape.p1.y, 'CURVE_P1')}
        {dot(shape.p2.x, shape.p2.y, 'CURVE_P2')}
        {control(shape.c1.x, shape.c1.y, 'CURVE_C1')}
        {control(shape.c2.x, shape.c2.y, 'CURVE_C2')}
      </g>
    )
  }
  if (shape.type === 'POLYLINE') {
    return (
      <g pointerEvents="all">
        {shape.points.map((p, i) => dot(p.x, p.y, `POLYLINE_VERTEX:${i}`))}
      </g>
    )
  }
  return null
}

const ZoomBadge = ({ zoom, onSet }: { zoom: number; onSet: (z: number) => void }) => (
  <div className="dh-zoom">
    <IconButton
      variant="tertiary"
      size="sm"
      label="Zoom out"
      onClick={() => onSet(Math.max(0.1, zoom * 0.9))}
      icon={<RemoveRoundedIcon fontSize="inherit" />}
    />
    <Button
      variant="tertiary"
      size="sm"
      aria-label="Reset zoom to 100%"
      title="Reset zoom"
    >
      {Math.round(zoom * 100)}%
    </Button>
    <IconButton
      variant="tertiary"
      size="sm"
      label="Zoom in"
      onClick={() => onSet(Math.min(8, zoom * 1.1))}
      icon={<AddRoundedIcon fontSize="inherit" />}
    />
  </div>
)

const boundsOfShapes = (shapes: DesignShape[]): BoxRect => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const s of shapes) {
    const b = shapeBoundingBox(s)
    minX = Math.min(minX, b.x)
    minY = Math.min(minY, b.y)
    maxX = Math.max(maxX, b.x + b.width)
    maxY = Math.max(maxY, b.y + b.height)
  }
  if (!Number.isFinite(minX)) return { x: 0, y: 0, width: 1, height: 1 }
  return { x: minX, y: minY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) }
}

const GridLayer = ({ viewport, size, grid }: { viewport: { zoom: number; x: number; y: number }; size: { w: number; h: number }; grid: number }) => {
  const minor = Math.max(4, grid || DESIGN_CANVAS_GRID)
  const major = minor * 5
  const minorScreen = minor * viewport.zoom
  if (minorScreen < 4) return null
  const startX = -viewport.x / viewport.zoom
  const startY = -viewport.y / viewport.zoom
  const endX = startX + size.w / viewport.zoom
  const endY = startY + size.h / viewport.zoom
  const firstMinorX = Math.floor(startX / minor) * minor
  const firstMinorY = Math.floor(startY / minor) * minor
  const minorLinesX: number[] = []
  const minorLinesY: number[] = []
  for (let x = firstMinorX; x <= endX; x += minor) minorLinesX.push(x)
  for (let y = firstMinorY; y <= endY; y += minor) minorLinesY.push(y)
  return (
    <g pointerEvents="none">
      {minorLinesX.map((x) => (
        <line key={`x${x}`} x1={x} y1={startY} x2={x} y2={endY} stroke={x % major === 0 ? 'color-mix(in srgb, var(--border) 50%, transparent)' : 'color-mix(in srgb, var(--border) 25%, transparent)'} strokeWidth={(x % major === 0 ? 1 : 0.5) / viewport.zoom} />
      ))}
      {minorLinesY.map((y) => (
        <line key={`y${y}`} x1={startX} y1={y} x2={endX} y2={y} stroke={y % major === 0 ? 'color-mix(in srgb, var(--border) 50%, transparent)' : 'color-mix(in srgb, var(--border) 25%, transparent)'} strokeWidth={(y % major === 0 ? 1 : 0.5) / viewport.zoom} />
      ))}
    </g>
  )
}
